// src/pages/AdminStudentRequests.jsx
import { useEffect, useState } from 'react';
import { fetchStudentRequests, updateRequestStatus } from '../../../services/adminService';
import './AdminStudentRequests.css';

function AdminStudentRequests() {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });
  const [sortBy, setSortBy] = useState('newest');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadRequests();
  }, []);

  useEffect(() => {
    filterAndSortRequests();
  }, [requests, filters, sortBy]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const data = await fetchStudentRequests();
      setRequests(data);
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: 'Error loading requests: ' + (err.response?.data?.msg || err.message) 
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortRequests = () => {
    let result = [...requests];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(req =>
        req.name?.toLowerCase().includes(searchTerm) ||
        req.email?.toLowerCase().includes(searchTerm) ||
        req.phone?.toLowerCase().includes(searchTerm) ||
        req.note?.toLowerCase().includes(searchTerm)
      );
    }

    // Status filter
    if (filters.status !== 'all') {
      result = result.filter(req => req.status === filters.status);
    }

    // Sorting
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        default:
          return 0;
      }
    });

    setFilteredRequests(result);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      setActionLoading(true);
      setMessage({ type: '', text: '' });
      
      await updateRequestStatus(id, newStatus);
      
      setMessage({ 
        type: 'success', 
        text: `Request ${newStatus === 'approved' ? 'approved' : 'rejected'}` 
      });
      
      // Update list
      setRequests(prev => prev.map(req => 
        req._id === id ? { ...req, status: newStatus } : req
      ));
      
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: 'Operation failed: ' + (err.response?.data?.msg || err.message) 
      });
      console.error('Failed to update status:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return { text: 'Pending', class: 'pending', icon: '⏳' };
      case 'approved':
        return { text: 'Approved', class: 'approved', icon: '✅' };
      case 'rejected':
        return { text: 'Rejected', class: 'rejected', icon: '❌' };
      default:
        return { text: status, class: 'unknown', icon: '❓' };
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'No date';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('tr-TR');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(req => req.status === 'pending').length,
    approved: requests.filter(req => req.status === 'approved').length,
    rejected: requests.filter(req => req.status === 'rejected').length
  };

  return (
    <div className="admin-requests-page">
      {/* Header */}
      <div className="requests-header">
        <div className="header-content">
          <h1>📥 Registration Requests</h1>
          <p>Manage student registration requests</p>
        </div>
        <button 
          onClick={loadRequests} 
          className="refresh-btn"
          disabled={loading}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Statistics */}
      <div className="requests-stats">
        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Requests</span>
          </div>
        </div>
        <div className="stat-card pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-info">
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
        <div className="stat-card approved">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <span className="stat-value">{stats.approved}</span>
            <span className="stat-label">Approved</span>
          </div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-icon">❌</div>
          <div className="stat-info">
            <span className="stat-value">{stats.rejected}</span>
            <span className="stat-label">Rejected</span>
          </div>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="requests-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email or phone..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filter-group">
          <select 
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <select 
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="name">Sort by Name (A-Z)</option>
          </select>
        </div>
      </div>

      {/* Message Notification */}
      {message.text && (
        <div className={`message ${message.type}`}>
          <span>{message.type === 'success' ? '✅' : '⚠️'}</span>
          <span>{message.text}</span>
          <button className="close-btn" onClick={() => setMessage({ type: '', text: '' })}>×</button>
        </div>
      )}

      {/* Requests List */}
      <div className="requests-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner">⏳</div>
            <p>Loading requests...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No requests found</h3>
            <p>
              {filters.search || filters.status !== 'all' 
                ? 'No requests match your search criteria.' 
                : 'No registration requests yet.'}
            </p>
          </div>
        ) : (
          <div className="requests-list">
            {filteredRequests.map((request) => {
              const statusInfo = getStatusInfo(request.status);
              
              return (
                <div key={request._id} className={`request-item ${statusInfo.class}`}>
                  <div className="request-header">
                    <div className="student-info">
                      <h3>{request.name}</h3>
                      <span className="email">{request.email}</span>
                    </div>
                    <div className="request-meta">
                      <span className={`status-badge ${statusInfo.class}`}>
                        {statusInfo.icon} {statusInfo.text}
                      </span>
                      <span className="time-ago">
                        {getTimeAgo(request.createdAt)}
                      </span>
                    </div>
                  </div>

                  <div className="request-details">
                    <div className="detail-group">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{request.phone || 'Not specified'}</span>
                    </div>
                    <div className="detail-group">
                      <span className="detail-label">Request Date:</span>
                      <span className="detail-value">{formatDate(request.createdAt)}</span>
                    </div>
                    {request.note && (
                      <div className="detail-group full-width">
                        <span className="detail-label">Note:</span>
                        <span className="detail-value note-text">{request.note}</span>
                      </div>
                    )}
                  </div>

                  {request.status === 'pending' && (
                    <div className="request-actions">
                      <button 
                        className="btn-approve"
                        onClick={() => handleStatusChange(request._id, 'approved')}
                        disabled={actionLoading}
                      >
                        {actionLoading ? '⏳' : '✅'} Approve
                      </button>
                      <button 
                        className="btn-reject"
                        onClick={() => handleStatusChange(request._id, 'rejected')}
                        disabled={actionLoading}
                      >
                        {actionLoading ? '⏳' : '❌'} Reject
                      </button>
                    </div>
                  )}

                  {request.status !== 'pending' && (
                    <div className="request-resolved">
                      <span>
                        {request.status === 'approved' ? '✅ Approved' : '❌ Rejected'} • 
                        {getTimeAgo(request.updatedAt || request.createdAt)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminStudentRequests;